import {
  Compass,
  Cpu,
  Megaphone,
  Sparkles,
  Palette,
} from "lucide-react";

const services = [
  {
    id: "strategy",
    eyebrow: "Clarity",
    icon: Compass,
    title: "Business Strategy",
    navigation: "Strategy",
    headline: "Map the move before the market does.",
    description:
      "We turn scattered goals into a focused operating plan, aligning leadership, positioning and execution around the highest-value opportunities.",
    image:
      "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1800&q=80",
    keywords: [
      "Positioning",
      "Market Research",
      "Roadmaps",
      "Operating Model",
      "Customer Journey",
      "Business Consulting",
    ],
    deliverables: [
      "Executive workshops and growth audits",
      "Priority maps with measurable milestones",
      "Decision frameworks for faster execution",
    ],
    metric: {
      value: "6x",
      label: "Faster alignment",
    },
  },
  {
    id: "technology",
    eyebrow: "Build",
    icon: Cpu,
    title: "Technology",
    navigation: "Technology",
    headline: "Premium digital systems that scale with confidence.",
    description:
      "From flagship websites to custom business platforms, we design technology that feels effortless on the surface and robust under the hood.",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1800&q=80",
    keywords: [
      "Web Platforms",
      "Custom Software",
      "Automation",
      "CRM",
      "API Integration",
      "Performance",
    ],
    deliverables: [
      "Conversion-led interfaces and product flows",
      "Scalable architecture and integrations",
      "Automation-ready foundations",
    ],
    metric: {
      value: "99%",
      label: "Platform reliability",
    },
  },
  {
    id: "marketing",
    eyebrow: "Demand",
    icon: Megaphone,
    title: "Marketing",
    navigation: "Marketing",
    headline: "Reach the right audience with measurable momentum.",
    description:
      "Integrated campaigns across SEO, paid media, content and analytics, built to grow brand visibility while improving the quality of every lead.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1800&q=80",
    keywords: [
      "SEO",
      "Performance Marketing",
      "Analytics",
      "Campaigns",
      "Lead Generation",
      "Content Strategy",
    ],
    deliverables: [
      "Search and paid channel strategy",
      "Landing pages with conversion focus",
      "Reporting that shows what moves revenue",
    ],
    metric: {
      value: "3.2x",
      label: "Better lead quality",
    },
  },
  {
    id: "ai",
    eyebrow: "Automate",
    icon: Sparkles,
    title: "AI & Automation",
    navigation: "AI",
    headline: "Practical AI that removes the repetitive work.",
    description:
      "We design intelligent workflows, assistants and knowledge systems that reduce manual effort and help teams move faster with better context.",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1800&q=80",
    keywords: [
      "AI Assistants",
      "Workflow Design",
      "Knowledge Systems",
      "Reporting",
      "Integrations",
      "Automation",
    ],
    deliverables: [
      "AI assistants tuned to your operations",
      "Workflow automations for repeat tasks",
      "Dashboards that surface the next best action",
    ],
    metric: {
      value: "40%",
      label: "Less manual effort",
    },
  },
  {
    id: "creative",
    eyebrow: "Brand",
    icon: Palette,
    title: "Brand & Creative",
    navigation: "Creative",
    headline: "Design systems that look expensive and feel clear.",
    description:
      "Identity, interfaces and motion assets that give your brand a consistent voice across every touchpoint, from launch to long-term growth.",
    image:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1800&q=80",
    keywords: [
      "Brand Identity",
      "UI/UX",
      "Design Systems",
      "Motion",
      "Visual Direction",
      "Creative Assets",
    ],
    deliverables: [
      "Identity systems with strong visual logic",
      "UI/UX direction for premium experiences",
      "Motion assets that make launches feel alive",
    ],
    metric: {
      value: "1",
      label: "Unified visual system",
    },
  },
];

export const principles = [
  {
    title: "Business first",
    description:
      "We start with outcomes, not channels. The work only matters if it improves the business.",
  },
  {
    title: "Design for trust",
    description:
      "Every touchpoint should feel premium, clear and confident from first glance to final action.",
  },
  {
    title: "Built to scale",
    description:
      "We create systems that can grow with the business instead of needing a redesign every quarter.",
  },
  {
    title: "Measure everything",
    description:
      "We pair creative direction with performance signals so decisions stay grounded in evidence.",
  },
];

export const workflow = [
  {
    step: "01",
    title: "Discover",
    text: "We audit the current state, identify friction and clarify the highest-value opportunities.",
  },
  {
    step: "02",
    title: "Design",
    text: "We shape the experience, content and system architecture around your growth goals.",
  },
  {
    step: "03",
    title: "Build",
    text: "We ship the core assets with a premium visual system and a production-ready setup.",
  },
  {
    step: "04",
    title: "Optimize",
    text: "We refine based on user behaviour, conversion signals and ongoing performance data.",
  },
];

export default services;
